import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { Language } from '../content';
import { prefersReducedMotion } from '../motion/reducedMotion';
import { Icon } from './Icon';

type ProductTourProps = { language: Language };

type TourItem = {
  icon: string;
  ar: { label: string; eyebrow: string; title: string; description: string; points: string[] };
  en: { label: string; eyebrow: string; title: string; description: string; points: string[] };
};

const items: TourItem[] = [
  {
    icon: 'reports',
    ar: {
      label: 'مركز العمليات',
      eyebrow: 'نظرة تنفيذية',
      title: 'اعرف ما يحدث الآن، وما يحتاج قرارك',
      description: 'لوحة تشغيل يومية تجمع الحالات والمواعيد والإنتاج والتحصيل في مشهد واحد واضح.',
      points: ['أولوية اليوم لحظة بلحظة', 'مؤشرات أداء بدون جداول منفصلة', 'تنبيهات عملية لا تشتت الفريق'],
    },
    en: {
      label: 'Operations hub',
      eyebrow: 'Executive view',
      title: 'See what is moving and what needs a decision',
      description: 'A live operating view of cases, due dates, production and collection in one calm workspace.',
      points: ['Today’s priorities at a glance', 'Performance without separate sheets', 'Alerts designed for action'],
    },
  },
  {
    icon: 'tooth',
    ar: {
      label: 'الطلبات والتركيبات',
      eyebrow: 'مسار كل حالة',
      title: 'كل تركيبة لها مسؤول وموعد وحالة واضحة',
      description: 'انتقل من الطلب الجديد إلى التصميم والتصنيع والتسليم دون أن تضيع ملاحظة أو مرفق.',
      points: ['خريطة أسنان مرتبطة بالحالة', 'إسناد واضح للفنيين', 'تاريخ كامل لكل تحديث'],
    },
    en: {
      label: 'Cases & restorations',
      eyebrow: 'Case workflow',
      title: 'Every restoration has an owner, date and status',
      description: 'Move from intake to design, production and delivery without losing a note or attachment.',
      points: ['Tooth chart linked to the case', 'Clear technician ownership', 'A complete update history'],
    },
  },
  {
    icon: 'invoice',
    ar: {
      label: 'الحسابات',
      eyebrow: 'وضوح مالي',
      title: 'العمل المنجز يتحول مباشرة إلى حساب دقيق',
      description: 'الفواتير والدفعات والأرصدة مرتبطة بالطلبات، حتى تعرف المستحق قبل نهاية اليوم.',
      points: ['فواتير تلقائية من تفاصيل الحالة', 'كشف حساب لكل عميل', 'متابعة التحصيل والمتأخرات'],
    },
    en: {
      label: 'Finance',
      eyebrow: 'Financial clarity',
      title: 'Completed work becomes accurate billing',
      description: 'Invoices, payments and balances stay connected to cases so collection is always visible.',
      points: ['Case-based invoicing', 'A ledger for every client', 'Collection and overdue tracking'],
    },
  },
  {
    icon: 'inventory',
    ar: {
      label: 'المخزون',
      eyebrow: 'مواد تحت السيطرة',
      title: 'اعرف النقص قبل أن يوقف الإنتاج',
      description: 'راقب المواد والموردين وحركة الصرف من نفس النظام الذي يدير العمل اليومي.',
      points: ['حد إعادة طلب لكل مادة', 'حركة مخزون قابلة للتتبع', 'مشتريات مرتبطة بالموردين'],
    },
    en: {
      label: 'Inventory',
      eyebrow: 'Materials in control',
      title: 'See shortages before they stop production',
      description: 'Track materials, suppliers and consumption inside the same system that runs daily work.',
      points: ['Reorder level per item', 'Traceable stock movement', 'Supplier-linked purchasing'],
    },
  },
];

function OperationsScreen({ ar }: { ar: boolean }) {
  return (
    <div className="tour-screen tour-screen--operations">
      <div className="tour-metrics">
        <article><span><Icon name="tooth" /></span><small>{ar ? 'حالات اليوم' : 'Today’s cases'}</small><strong>48</strong><em>+12%</em></article>
        <article><span><Icon name="clock" /></span><small>{ar ? 'تسليم اليوم' : 'Due today'}</small><strong>16</strong><em>{ar ? '4 عاجلة' : '4 urgent'}</em></article>
        <article><span><Icon name="invoice" /></span><small>{ar ? 'تحصيل اليوم' : 'Today’s collection'}</small><strong>24.8K</strong><em>+8.4%</em></article>
      </div>
      <div className="tour-screen__grid">
        <article className="tour-panel tour-panel--chart">
          <header><div><small>{ar ? 'أداء الإنتاج' : 'Production performance'}</small><strong>76%</strong></div><span>{ar ? 'آخر 7 أيام' : 'Last 7 days'}</span></header>
          <div className="tour-bars" aria-hidden="true">{[36, 48, 43, 62, 57, 77, 88, 74, 92, 83].map((height, index) => <i key={index} style={{ height: `${height}%` }} />)}</div>
          <footer><span>{ar ? 'السبت' : 'Sat'}</span><span>{ar ? 'اليوم' : 'Today'}</span></footer>
        </article>
        <article className="tour-panel tour-panel--queue">
          <header><strong>{ar ? 'الأولوية الآن' : 'Priority now'}</strong><span><Icon name="arrow" /></span></header>
          {[
            [ar ? 'جسر زيركون — 14 وحدة' : 'Zirconia bridge — 14 units', '#DL-2841', 'urgent'],
            [ar ? 'تاج E-max' : 'E-max crown', '#DL-2837', 'active'],
            [ar ? 'طقم كامل' : 'Full denture', '#DL-2829', 'calm'],
          ].map(([name, id, tone]) => <div className="tour-queue" key={id}><i className={`is-${tone}`} /><span><strong>{name}</strong><small>{id}</small></span><em>{ar ? 'قيد العمل' : 'In progress'}</em></div>)}
        </article>
      </div>
    </div>
  );
}

function CasesScreen({ ar }: { ar: boolean }) {
  const columns = [
    { title: ar ? 'طلبات جديدة' : 'New requests', count: 8, tone: 'cyan', cards: [[ar ? 'زيركون — 6 وحدات' : 'Zirconia — 6 units', '#DL-2847'], [ar ? 'Inlay خزف' : 'Ceramic inlay', '#DL-2844']] },
    { title: ar ? 'قيد التصنيع' : 'In production', count: 14, tone: 'violet', cards: [[ar ? 'جسر — 14 وحدة' : 'Bridge — 14 units', '#DL-2841'], [ar ? 'طقم مرن' : 'Flexible denture', '#DL-2836']] },
    { title: ar ? 'جاهز للتسليم' : 'Ready to deliver', count: 6, tone: 'green', cards: [[ar ? 'E-max — وحدتان' : 'E-max — 2 units', '#DL-2839'], [ar ? 'معدن بورسلين' : 'PFM restoration', '#DL-2831']] },
  ];
  return (
    <div className="tour-screen tour-screen--cases">
      <div className="tour-board">
        {columns.map((column) => <section key={column.title}><header><span><i className={`is-${column.tone}`} />{column.title}</span><b>{column.count}</b></header>{column.cards.map(([name, id], index) => <article key={id}><div><span className="tour-avatar">{index + 1}</span><small>{id}</small></div><strong>{name}</strong><footer><span><Icon name="clock" />{index ? (ar ? 'غدًا' : 'Tomorrow') : (ar ? 'اليوم 4:30' : 'Today 4:30')}</span><span><Icon name="team" />{index + 2}</span></footer></article>)}</section>)}
      </div>
    </div>
  );
}

function FinanceScreen({ ar }: { ar: boolean }) {
  return (
    <div className="tour-screen tour-screen--finance">
      <div className="finance-head"><div><small>{ar ? 'إجمالي التحصيل — هذا الشهر' : 'Total collection — this month'}</small><strong>186,420 <em>{ar ? 'ج.م' : 'EGP'}</em></strong><span><Icon name="trend" />+18.4%</span></div><div className="finance-ring"><strong>82%</strong><small>{ar ? 'تم التحصيل' : 'collected'}</small></div></div>
      <div className="finance-grid">
        <article className="tour-panel finance-chart"><header><strong>{ar ? 'حركة الإيرادات' : 'Revenue movement'}</strong><span>{ar ? 'أسبوعي' : 'Weekly'}</span></header><div className="finance-line" aria-hidden="true"><i /><i /><i /><i /><i /><i /><i /></div><footer><span>32K</span><span>48K</span><span>64K</span></footer></article>
        <article className="tour-panel finance-ledger"><header><strong>{ar ? 'أحدث الدفعات' : 'Recent payments'}</strong><Icon name="invoice" /></header>{[[ar ? 'مختبر النخبة' : 'Elite Clinic','12,400'],[ar ? 'مركز الابتسامة' : 'Smile Center','8,750'],[ar ? 'عيادة كير' : 'Care Clinic','6,200']].map(([name, value], index) => <div key={name}><span><i>{index + 1}</i><strong>{name}</strong></span><b>+{value}</b></div>)}</article>
      </div>
    </div>
  );
}

function InventoryScreen({ ar }: { ar: boolean }) {
  const rows = [[ar ? 'أقراص زيركون A2' : 'Zirconia discs A2','24','68%','ok'],[ar ? 'مسحوق بورسلين' : 'Ceramic powder','8','32%','low'],[ar ? 'بلوكات E-max' : 'E-max blocks','42','84%','ok'],[ar ? 'أكريلك حراري' : 'Thermal acrylic','11','46%','watch']];
  return (
    <div className="tour-screen tour-screen--inventory">
      <div className="inventory-summary"><article><Icon name="inventory" /><span><small>{ar ? 'أصناف نشطة' : 'Active items'}</small><strong>248</strong></span></article><article><Icon name="bell" /><span><small>{ar ? 'تحتاج طلبًا' : 'Need reorder'}</small><strong>7</strong></span></article><article><Icon name="payment" /><span><small>{ar ? 'طلبات مفتوحة' : 'Open POs'}</small><strong>12</strong></span></article></div>
      <article className="tour-panel inventory-table"><header><strong>{ar ? 'مراقبة المخزون' : 'Stock monitoring'}</strong><span>{ar ? 'تحديث مباشر' : 'Live update'}<i /></span></header>{rows.map(([name, count, level, tone]) => <div className="inventory-row" key={name}><span><i className={`is-${tone}`}><Icon name="inventory" /></i><strong>{name}</strong></span><small>{count} {ar ? 'وحدة' : 'units'}</small><div><b style={{ width: level }} /><em>{level}</em></div></div>)}</article>
    </div>
  );
}

function ProductScreen({ index, language }: { index: number; language: Language }) {
  const ar = language === 'ar';
  if (index === 1) return <CasesScreen ar={ar} />;
  if (index === 2) return <FinanceScreen ar={ar} />;
  if (index === 3) return <InventoryScreen ar={ar} />;
  return <OperationsScreen ar={ar} />;
}

export function ProductTour({ language }: ProductTourProps) {
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  const ar = language === 'ar';

  useEffect(() => {
    if (paused || prefersReducedMotion()) return undefined;
    const timer = window.setInterval(() => setActive((current) => (current + 1) % items.length), 6800);
    return () => window.clearInterval(timer);
  }, [paused]);

  const selected = items[active][language];

  return (
    <section className="product-tour section" aria-labelledby="product-tour-title">
      <div className="product-tour__glow" aria-hidden="true" />
      <div className="container">
        <div className="section-heading">
          <span className="eyebrow"><Icon name="devices" />{ar ? 'داخل النظام' : 'Inside the platform'}</span>
          <h2 id="product-tour-title">{ar ? 'شاهد المختبر كما يجب أن يُدار' : 'See how a modern lab should run'}</h2>
          <p>{ar ? 'أربع واجهات مترابطة تمنح الإدارة والفنيين صورة واحدة للحقيقة.' : 'Four connected workspaces give managers and technicians one operational truth.'}</p>
        </div>

        <div className="product-tour__tabs" role="tablist" aria-label={ar ? 'شاشات النظام' : 'Platform screens'} onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}>
          {items.map((item, index) => {
            const label = item[language].label;
            return <button type="button" role="tab" aria-selected={active === index} aria-controls="product-tour-panel" className={active === index ? 'is-active' : ''} onClick={() => setActive(index)} onFocus={() => setPaused(true)} onBlur={() => setPaused(false)} key={label}><span><Icon name={item.icon} /></span><strong>{label}</strong>{active === index && <i aria-hidden="true" />}</button>;
          })}
        </div>

        <div className="product-tour__layout reveal" id="product-tour-panel" role="tabpanel" onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}>
          <div className="product-tour__frame">
            <div className="product-tour__chrome"><span /><span /><span /><strong>Dental Lab System</strong><div><Icon name="search" /><i /><i /></div></div>
            <aside className="product-tour__rail" aria-hidden="true"><b><Icon name="tooth" /></b>{items.map((item, index) => <span className={active === index ? 'is-active' : ''} key={item.icon}><Icon name={item.icon} /></span>)}</aside>
            <div className="product-tour__screen" key={active}><ProductScreen index={active} language={language} /></div>
            <div className="product-tour__live"><i />{ar ? 'بيانات حية' : 'Live data'}</div>
          </div>
          <div className="product-tour__copy" key={`copy-${active}`}>
            <span>{selected.eyebrow}</span>
            <h3>{selected.title}</h3>
            <p>{selected.description}</p>
            <ul>{selected.points.map((point) => <li key={point}><i><Icon name="check" /></i>{point}</li>)}</ul>
            <Link className="text-link" to="/features">{ar ? 'استكشف كل المميزات' : 'Explore every feature'}<Icon name="arrow" /></Link>
          </div>
        </div>
      </div>
    </section>
  );
}
